import React, { useState, useRef } from 'react';
import { Box, Typography, Card } from '@mui/material';

const GlowEdge = ({ side, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      ...(side === 'left' ? { left: -14 } : { right: -14 }),
      width: 28,
      height: 28,
      borderRadius: '50%',
      background: 'radial-gradient(circle, rgba(126, 181, 216, 0.55) 0%, rgba(126, 181, 216, 0.15) 50%, transparent 75%)',
      cursor: 'pointer',
      animation: 'glowPulse 2.2s ease-in-out infinite',
      '@keyframes glowPulse': {
        '0%, 100%': { opacity: 0.6, transform: 'translateY(-50%) scale(1)' },
        '50%': { opacity: 1, transform: 'translateY(-50%) scale(1.35)' },
      },
      zIndex: 2,
    }}
  />
);

function VerticalCarousel({ items, title }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const touchStartX = useRef(null);

  const goTo = (index) => setCurrentIndex(Math.max(0, Math.min(items.length - 1, index)));

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.touches[0].clientX - touchStartX.current;
    // Resist dragging past the first/last card
    if ((currentIndex === 0 && delta > 0) || (currentIndex === items.length - 1 && delta < 0)) {
      setDragOffset(delta * 0.25);
    } else {
      setDragOffset(delta);
    }
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    setIsDragging(false);
    setDragOffset(0);
    if (delta > 50) goTo(currentIndex + 1);
    else if (delta < -50) goTo(currentIndex - 1);
    touchStartX.current = null;
  };

  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < items.length - 1;

  // Track translateX: base slide position + live drag offset
  const baseTranslate = -(currentIndex * (100 / items.length));

  return (
    <Box
      sx={{
        width: '100%',
        bgcolor: '#111827',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: { xs: 5, sm: 7 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          maxWidth: '1200px',
          px: { xs: 2, sm: 4 },
        }}
      >
        {title && (
          <Typography
            variant="h2"
            sx={{
              color: 'white',
              fontWeight: 300,
              mb: { xs: 2, sm: 3 },
              textAlign: 'center',
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            }}
          >
            {title}
          </Typography>
        )}

        {/* Viewport */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: { xs: '100%', sm: '750px', md: '850px' },
            overflow: 'hidden',
          }}
        >
          {canGoPrev && <GlowEdge side="left" onClick={() => goTo(currentIndex - 1)} />}
          {canGoNext && <GlowEdge side="right" onClick={() => goTo(currentIndex + 1)} />}

          {/* Sliding track */}
          <Box
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            sx={{
              display: 'flex',
              alignItems: 'stretch',
              width: `${items.length * 100}%`,
              transform: `translateX(calc(${baseTranslate}% + ${dragOffset}px))`,
              transition: isDragging ? 'none' : 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              touchAction: 'pan-y',
              willChange: 'transform',
            }}
          >
            {items.map((item, index) => (
              <Box key={index} sx={{ width: `${100 / items.length}%`, display: 'flex' }}>
                <Card
                  elevation={0}
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    pt: { xs: 4, sm: 5 },
                    pb: { xs: 4, sm: 5 },
                    px: { xs: 3, sm: 5, md: 6 },
                    background: 'linear-gradient(135deg, #1E2D40 0%, #1A3050 100%)',
                    border: '1px solid rgba(148, 163, 184, 0.18)',
                    color: 'white',
                    borderRadius: { xs: 0, sm: 3 },
                  }}
                >
                  <Typography
                    variant="h3"
                    component="h3"
                    sx={{
                      fontWeight: 700,
                      mb: 3,
                      textAlign: 'center',
                      fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
                      lineHeight: 1.3,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      lineHeight: 1.8,
                      textAlign: 'center',
                      fontSize: { xs: '1.05rem', sm: '1.15rem', md: '1.25rem' },
                      maxWidth: { xs: '100%', sm: '600px' },
                      mx: 'auto',
                    }}
                  >
                    {item.description}
                  </Typography>
                </Card>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          {items.map((_, index) => (
            <Box
              key={index}
              onClick={() => goTo(index)}
              sx={{
                width: { xs: 10, sm: 12 },
                height: { xs: 10, sm: 12 },
                borderRadius: '50%',
                bgcolor: index === currentIndex ? 'white' : 'rgba(255,255,255,0.4)',
                cursor: 'pointer',
                border: '2px solid white',
                transition: 'background-color 0.2s ease',
                '&:hover': { bgcolor: 'white' },
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default VerticalCarousel;
